import { useState, useCallback, type FC } from 'react';
import { useCustom, useCustomMutation } from '@refinedev/core';
import { Button, Segmented, Spin, message } from 'antd';
import { Plus, LayoutGrid, Clock } from 'lucide-react';
import RoadmapProgressBar from './RoadmapProgressBar';
import RoadmapTimelineView from './RoadmapTimelineView';
import RoadmapKanbanView from './RoadmapKanbanView';
import CreateMilestoneModal, { type MilestoneFormData } from './CreateMilestoneModal';
import type { Milestone } from './MilestoneCard';

interface Roadmap {
  id: string;
  title: string;
  description?: string | null;
  status: string;
  startDate: string;
  endDate?: string | null;
  milestones: Milestone[];
}

interface RoadmapDetailViewProps {
  roadmapId: string;
  onBack: () => void;
}

const RoadmapDetailView: FC<RoadmapDetailViewProps> = ({ roadmapId, onBack }) => {
  const [view, setView] = useState<'timeline' | 'kanban'>('kanban');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState<Milestone | null>(null);
  const { mutate: createMilestone, isLoading: isCreating } = useCustomMutation();
  const { mutate: updateMilestone } = useCustomMutation();
  const { mutate: deleteMilestone } = useCustomMutation();

  const { data, isLoading, refetch } = useCustom<Roadmap>({
    url: `/api/v1/admin/growth/roadmaps/${roadmapId}`,
    method: 'get',
    queryOptions: { queryKey: ['roadmap', roadmapId] },
  });

  const roadmap: Roadmap | undefined = (data?.data as unknown as Roadmap) ?? undefined;
  const milestones: Milestone[] = roadmap?.milestones ?? [];
  const completedCount = milestones.filter((m) => m.status === 'completed').length;

  const handleCreate = useCallback(
    (formData: MilestoneFormData) => {
      createMilestone(
        { url: `/api/v1/admin/growth/roadmaps/${roadmapId}/milestones`, method: 'post', values: formData },
        {
          onSuccess: () => { message.success('Jalon cree'); setModalOpen(false); refetch(); },
          onError: () => message.error('Erreur lors de la creation'),
        },
      );
    },
    [createMilestone, roadmapId, refetch],
  );

  const handleEdit = useCallback((m: Milestone) => { setEditingMilestone(m); setModalOpen(true); }, []);

  const handleUpdate = useCallback(
    (formData: MilestoneFormData) => {
      if (!editingMilestone) return;
      updateMilestone(
        { url: `/api/v1/admin/growth/roadmaps/${roadmapId}/milestones/${editingMilestone.id}`, method: 'patch', values: formData },
        {
          onSuccess: () => { message.success('Jalon modifie'); setModalOpen(false); setEditingMilestone(null); refetch(); },
          onError: () => message.error('Erreur lors de la modification'),
        },
      );
    },
    [updateMilestone, roadmapId, editingMilestone, refetch],
  );

  const handleDelete = useCallback(
    (mId: string) => {
      deleteMilestone(
        { url: `/api/v1/admin/growth/roadmaps/${roadmapId}/milestones/${mId}`, method: 'delete', values: {} },
        {
          onSuccess: () => { message.success('Jalon supprime'); refetch(); },
          onError: () => message.error('Erreur lors de la suppression'),
        },
      );
    },
    [deleteMilestone, roadmapId, refetch],
  );

  if (isLoading) {
    return <div className="flex h-64 items-center justify-center"><Spin size="large" /></div>;
  }

  if (!roadmap) {
    return <div className="fc-card p-6 text-center text-slate-500 dark:text-slate-400">Feuille de route introuvable.</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-1 min-w-0">
          <button onClick={onBack} className="text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 mb-1">
            &larr; Retour aux feuilles de route
          </button>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white truncate">{roadmap.title}</h2>
          {roadmap.description && (
            <p className="text-sm text-slate-500 dark:text-slate-400 line-clamp-2">{roadmap.description}</p>
          )}
        </div>
        <Button type="primary" icon={<Plus className="w-4 h-4" />} onClick={() => { setEditingMilestone(null); setModalOpen(true); }}>
          Nouveau jalon
        </Button>
      </div>

      {/* Progress + Toggle */}
      <div className="fc-card p-4 flex items-center gap-6 flex-wrap" data-tour="roadmap-progress">
        <div className="flex-1 min-w-[200px]">
          <RoadmapProgressBar completed={completedCount} total={milestones.length} />
        </div>
        <Segmented
          value={view}
          onChange={(v) => setView(v as 'timeline' | 'kanban')}
          options={[
            { value: 'kanban', label: <span className="inline-flex items-center gap-1.5"><LayoutGrid className="w-3.5 h-3.5" /> Kanban</span> },
            { value: 'timeline', label: <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Timeline</span> },
          ]}
        />
      </div>

      {/* View */}
      <div data-tour="roadmap-kanban">
      {view === 'kanban' ? (
        <RoadmapKanbanView milestones={milestones} onEdit={handleEdit} onDelete={handleDelete} />
      ) : (
        <RoadmapTimelineView milestones={milestones} onEdit={handleEdit} onDelete={handleDelete} />
      )}
      </div>

      {/* Modal */}
      <CreateMilestoneModal
        open={modalOpen}
        loading={isCreating}
        onOk={editingMilestone ? handleUpdate : handleCreate}
        onCancel={() => { setModalOpen(false); setEditingMilestone(null); }}
        title={editingMilestone ? 'Modifier le jalon' : 'Nouveau jalon'}
        initialData={editingMilestone ? {
          title: editingMilestone.title,
          description: editingMilestone.description ?? '',
          category: editingMilestone.category,
          dueDate: editingMilestone.dueDate ?? null,
          linkedModule: editingMilestone.linkedModule ?? null,
          linkedEntityId: editingMilestone.linkedEntityId ?? null,
          assignee: editingMilestone.assignee ?? null,
        } : undefined}
      />
    </div>
  );
};

export default RoadmapDetailView;
